import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createBlock as _createBlock, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "d-flex align-center mb-3" }
const _hoisted_2 = {
  key: 0,
  class: "text-center py-5"
}
const _hoisted_3 = { class: "font-weight-bold mb-1" }
const _hoisted_4 = { class: "pl-7" }
const _hoisted_5 = {
  key: 0,
  class: "grey--text caption ml-3"
}
const _hoisted_6 = { style: {"max-height":"calc(100vh - 450px) !important","overflow-y":"scroll"} }
const _hoisted_7 = { class: "font-weight-bold mb-2" }
const _hoisted_8 = { key: 0 }

import { ref, computed, reactive, watch, onMounted } from 'vue';
import { useUsersStore } from '@/store/users';
import debounce from 'lodash/debounce';


export default /*@__PURE__*/_defineComponent({
  __name: 'Reassignments',
  setup(__props) {

const usersStore = useUsersStore();

const userDonor = ref<number | null>(null);
const userReceiver = ref<number | null>(null);

const donorUsers = ref<any[]>([]);
const receiverUsers = ref<any[]>([]);
const searchDonor = ref('');
const searchReceiver = ref('');
const loadingDonorUsers = ref(false);
const loadingReceiverUsers = ref(false);

const loadingRecords = ref(false);
const transferring = ref(false);
const showTransferAllDialog = ref(false);

const recordTypes = [
  { key: 'clients', label: 'Clientes' },
  { key: 'quotations', label: 'Cotizaciones' },
  { key: 'opportunities', label: 'Oportunidades' },
  { key: 'activities', label: 'Actividades' },
  { key: 'leads', label: 'Prospectos' },
  { key: 'sales', label: 'Ventas' },
];

const records = reactive({
  clients: [],
  quotations: [],
  opportunities: [],
  activities: [],
  leads: [],
  sales: [],
}) as any;

// Seleccionados por grupo
const selected = reactive({
  clients: [],
  quotations: [],
  opportunities: [],
  activities: [],
  leads: [],
  sales: [],
}) as any;

// Estados de colapso
const collapsed = reactive({
  clients: true,
  quotations: true,
  opportunities: true,
  activities: true,
  leads: true,
  sales: true,
});

// Selección de grupo (checkbox header de cada grupo)
const groupSelection = reactive({
  clients: false,
  quotations: false,
  opportunities: false,
  activities: false,
  leads: false,
  sales: false,
});

// Checkbox global
const selectAll = ref(false);

const mergeSelectedUser = (items: any[], selectedId: number | null, currentItems: any[]) => {
  if (!selectedId) return items;
  const selected = currentItems.find((user: any) => Number(user.id) === Number(selectedId));
  if (!selected || items.some((user: any) => Number(user.id) === Number(selectedId))) {
    return items;
  }
  return [selected, ...items];
};

const fetchUsers = async (search = '') => {
  const params = search ? { 'filter[name]': search } : {};
  const { data } = await usersStore.searchUsers(params);
  return data;
};

const fetchDonorUsers = async (search = '') => {
  loadingDonorUsers.value = true;
  try {
    const data = await fetchUsers(search);
    donorUsers.value = mergeSelectedUser(data, userDonor.value, donorUsers.value);
  } finally {
    loadingDonorUsers.value = false;
  }
};

const fetchReceiverUsers = async (search = '') => {
  loadingReceiverUsers.value = true;
  try {
    const data = await fetchUsers(search);
    receiverUsers.value = mergeSelectedUser(data, userReceiver.value, receiverUsers.value);
  } finally {
    loadingReceiverUsers.value = false;
  }
};

const fetchDonorUsersDebounced = debounce((q) => fetchDonorUsers(q), 300);
const fetchReceiverUsersDebounced = debounce((q) => fetchReceiverUsers(q), 300);

const userFullName = (user: any) =>
  [user?.name, user?.last_name ?? user?.last].filter(Boolean).join(' ') || user?.name || '';

const onSelectDonor = async (id: number) => {
  Object.keys(records).forEach(key => {
    records[key] = [];
    selected[key] = [];
    collapsed[key] = true;
    groupSelection[key] = false;
  });
  if (!id) return;
  loadingRecords.value = true;
  try {
    const data = await usersStore.fetchAssignedRecords(id) as any;
    recordTypes.forEach(type => {
      records[type.key] = data[type.key]?.items || [];
      selected[type.key] = [];
      collapsed[type.key] = true;
      groupSelection[type.key] = false;
    });
  } finally {
    loadingRecords.value = false;
  }
};

function subjectName(subject: any): string {
  const attributes = subject?.attributes ?? subject;

  return attributes?.commercial_name
    || attributes?.legal_name
    || [attributes?.name, attributes?.last].filter(Boolean).join(' ')
    || '';
}

function withSubject(label: string, subject: any): string {
  const name = subjectName(subject);
  return name ? `${label} - ${name}` : label;
}

// Etiqueta amigable por tipo
function typeRecordLabel(type:any, record:any) {
  if (!record) return '';
  if (type === 'clients') {
    return record.attributes?.commercial_name || record.attributes?.legal_name || `ID ${record.id}`;
  }
  if (type === 'quotations') {
    return withSubject(`Cotización #${record.id}`, record.subject);
  }
  if (type === 'opportunities') {
    return withSubject(record.opportunity_name || `Oportunidad #${record.id}`, record.subject);
  }
  if (type === 'activities') {
    return withSubject(record.title || record.description || `Actividad #${record.id}`, record.subject);
  }
  if (type === 'leads') {
    return [record.name, record.last].filter(Boolean).join(' ') || `ID ${record.id}`;
  }
  if (type === 'sales') {
    return withSubject(`Venta #${record.id}`, record.client || record.account || record.quotation?.subject);
  }
  return record.id;
}

// --- Group & Global select logic ---

// Actualiza la selección global según lo seleccionado en grupos
watch(selected, () => {
  const allSelected = recordTypes.every(
    t => selected[t.key].length === records[t.key].length && records[t.key].length > 0
  );
  const noneSelected = recordTypes.every(
    t => selected[t.key].length === 0
  );
  selectAll.value = allSelected;
}, { deep: true });

// Para cada grupo, mantiene actualizada la selección del checkbox header
watch(selected, () => {
  recordTypes.forEach(type => {
    groupSelection[type.key] =
      selected[type.key].length === records[type.key].length && records[type.key].length > 0;
  });
}, { deep: true });

// Indeterminado para grupo
const isIndeterminateGroup = (key: string) =>
  selected[key].length > 0 && selected[key].length < records[key].length;

// Indeterminado para global
const isIndeterminateAll = computed(() => {
  const total = recordTypes.reduce((sum, t) => sum + records[t.key].length, 0);
  const selectedTotal = recordTypes.reduce((sum, t) => sum + selected[t.key].length, 0);
  return selectedTotal > 0 && selectedTotal < total;
});

// Selecciona/deselecciona todo global
function toggleSelectAll() {
  if (selectAll.value) {
    // Seleccionar todos
    recordTypes.forEach(type => {
      selected[type.key] = records[type.key].map((r: any) => r.id);
    });
  } else {
    // Deseleccionar todos
    recordTypes.forEach(type => {
      selected[type.key] = [];
    });
  }
}

// Selecciona/deselecciona todos en grupo
function toggleGroupSelect(key: string) {
  if (groupSelection[key]) {
    selected[key] = records[key].map((r: any) => r.id);
  } else {
    selected[key] = [];
  }
}

// Expand/collapse grupos
function toggleCollapse(key: string) {
  collapsed[key] = !collapsed[key];
}

// Quitar seleccionado desde el lado derecho
function removeSelected(key: string, id: any) {
  selected[key] = selected[key].filter((item: any) => item !== id);
}

// Obtener registro por id
function getRecordById(type: string, id: any) {
  return records[type].find((r: any) => r.id === id);
}

const totalSelected = computed(() =>
  recordTypes.reduce((sum, t) => sum + (selected[t.key]?.length || 0), 0)
);

const totalRecords = computed(() =>
  recordTypes.reduce((sum, type) => sum + (records[type.key]?.length || 0), 0)
);

const transferRecords = async () => {
  if (!userDonor.value || !userReceiver.value || totalSelected.value === 0) return;
  transferring.value = true;
  try {
    // SOLO MANDAR LOS QUE TIENEN ALGO SELECCIONADO
    const selectedToSend = {};
    Object.keys(selected).forEach(key => {
      if (selected[key] && selected[key].length > 0) {
        selectedToSend[key] = selected[key];
      }
    });
    await usersStore.reassignRecords({
      from_user_id: userDonor.value,
      to_user_id: userReceiver.value,
      selected: selectedToSend,
    });
    onSelectDonor(userDonor.value);
    userReceiver.value = null;
    Object.keys(selected).forEach(k => selected[k] = []);
  } catch (e) {}
  finally { transferring.value = false; }
};

const transferAllRecords = async () => {
  if (!userDonor.value || !userReceiver.value || totalRecords.value === 0) return;
  transferring.value = true;
  try {
    await usersStore.reassignRecords({
      from_user_id: userDonor.value,
      to_user_id: userReceiver.value,
      transfer_all: true,
    });
    showTransferAllDialog.value = false;
    await onSelectDonor(userDonor.value);
    userReceiver.value = null;
    Object.keys(selected).forEach(k => selected[k] = []);
  } catch (e) {
    // The store displays the API error to the user.
  } finally {
    transferring.value = false;
  }
};


onMounted(() => {
  fetchDonorUsers();
  fetchReceiverUsers();
});

return (_ctx: any,_cache: any) => {
  const _component_v_autocomplete = _resolveComponent("v-autocomplete")!
  const _component_v_divider = _resolveComponent("v-divider")!
  const _component_v_checkbox = _resolveComponent("v-checkbox")!
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_expand_transition = _resolveComponent("v-expand-transition")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_dialog = _resolveComponent("v-dialog")!
  const _component_v_container = _resolveComponent("v-container")!

  return (_openBlock(), _createBlock(_component_v_container, { class: "px-12" }, {
    default: _withCtx(() => [
      _cache[11] || (_cache[11] = _createElementVNode("h2", { class: "mb-4" }, "Reasignación de Registros", -1)),
      _createVNode(_component_v_row, { class: "mx-0 my-3" }, {
        default: _withCtx(() => [
          _createVNode(_component_v_col, {
            cols: "12",
            md: "6",
            class: "pa-0 pr-md-2 mb-4 mb-md-0"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_card, { class: "pa-6" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_autocomplete, {
                    modelValue: userDonor.value,
                    "onUpdate:modelValue": [
                      _cache[0] || (_cache[0] = ($event: any) => ((userDonor).value = $event)),
                      onSelectDonor
                    ],
                    items: donorUsers.value,
                    loading: loadingDonorUsers.value,
                    search: searchDonor.value,
                    "onUpdate:search": [
                      _cache[1] || (_cache[1] = ($event: any) => ((searchDonor).value = $event)),
                      _unref(fetchDonorUsersDebounced)
                    ],
                    "item-title": userFullName,
                    "item-value": "id",
                    label: "Usuario donador",
                    "hide-details": "",
                    clearable: "",
                    variant: "solo-filled"
                  }, null, 8, ["modelValue", "items", "loading", "search", "onUpdate:search"]),
                  _createVNode(_component_v_divider, { class: "my-4" }),
                  _createElementVNode("div", _hoisted_1, [
                    _createVNode(_component_v_checkbox, {
                      modelValue: selectAll.value,
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((selectAll).value = $event)),
                      indeterminate: isIndeterminateAll.value,
                      "hide-details": "",
                      label: "Seleccionar todo",
                      class: "mr-2",
                      density: "compact",
                      onChange: toggleSelectAll
                    }, null, 8, ["modelValue", "indeterminate"])
                  ]),
                  (loadingRecords.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                        _createVNode(_component_v_progress_circular, {
                          indeterminate: "",
                          color: "primary"
                        })
                      ]))
                    : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, _renderList(recordTypes, (type) => {
                        return _createElementVNode("div", {
                          key: type?.key,
                          class: "mb-3"
                        }, [
                          _createVNode(_component_v_row, { class: "d-flex align-center ma-0" }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_checkbox, {
                                modelValue: groupSelection[type.key],
                                "onUpdate:modelValue": ($event: any) => ((groupSelection[type.key]) = $event),
                                indeterminate: isIndeterminateGroup(type.key),
                                "hide-details": "",
                                class: "mr-2",
                                density: "compact",
                                onChange: ($event: any) => (toggleGroupSelect(type.key))
                              }, null, 8, ["modelValue", "onUpdate:modelValue", "indeterminate", "onChange"]),
                              _createElementVNode("div", _hoisted_3, _toDisplayString(type.label + ' (' + records[type.key].length + ')'), 1),
                              _createVNode(_component_v_spacer),
                              _createVNode(_component_v_btn, {
                                icon: "",
                                size: "small",
                                variant: "text",
                                onClick: ($event: any) => (toggleCollapse(type.key)),
                                class: "mr-2"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_icon, null, {
                                    default: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(collapsed[type.key] ? 'mdi-chevron-down' : 'mdi-chevron-up'), 1)
                                    ]),
                                    _: 2
                                  }, 1024)
                                ]),
                                _: 2
                              }, 1032, ["onClick"])
                            ]),
                            _: 2
                          }, 1024),
                          _createVNode(_component_v_expand_transition, null, {
                            default: _withCtx(() => [
                              _withDirectives(_createElementVNode("div", _hoisted_4, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(records[type.key] || [], (record) => {
                                  return (_openBlock(), _createBlock(_component_v_checkbox, {
                                    key: record.id,
                                    modelValue: selected[type.key],
                                    "onUpdate:modelValue": ($event: any) => ((selected[type.key]) = $event),
                                    label: typeRecordLabel(type.key, record),
                                    value: record.id,
                                    density: "compact",
                                    "hide-details": ""
                                  }, null, 8, ["modelValue", "onUpdate:modelValue", "label", "value"]))
                                }), 128)),
                                (!(records[type.key]?.length))
                                  ? (_openBlock(), _createElementBlock("div", _hoisted_5, "Sin registros"))
                                  : _createCommentVNode("", true)
                              ], 512), [
                                [_vShow, !collapsed[type.key]]
                              ])
                            ]),
                            _: 2
                          }, 1024)
                        ])
                      }), 64))
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            md: "6",
            class: "pa-0 pl-md-2"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_card, { class: "pa-6" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_autocomplete, {
                    variant: "solo-filled",
                    modelValue: userReceiver.value,
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((userReceiver).value = $event)),
                    items: receiverUsers.value,
                    loading: loadingReceiverUsers.value,
                    search: searchReceiver.value,
                    "onUpdate:search": [
                      _cache[4] || (_cache[4] = ($event: any) => ((searchReceiver).value = $event)),
                      _unref(fetchReceiverUsersDebounced)
                    ],
                    "item-title": userFullName,
                    "item-value": "id",
                    label: "Usuario receptor",
                    "hide-details": "",
                    clearable: "",
                    disabled: !userDonor.value
                  }, null, 8, ["modelValue", "items", "loading", "search", "disabled", "onUpdate:search"]),
                  _createVNode(_component_v_divider, { class: "my-4" }),
                  _createElementVNode("div", _hoisted_6, [
                    (_openBlock(), _createElementBlock(_Fragment, null, _renderList(recordTypes, (type) => {
                      return _createElementVNode("div", {
                        key: type?.key
                      }, [
                        _createElementVNode("div", _hoisted_7, _toDisplayString(type.label), 1),
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selected[type.key], (id) => {
                          return (_openBlock(), _createBlock(_component_v_chip, {
                            key: id,
                            class: "ma-1",
                            closable: "",
                            "onClick:close": ($event: any) => (removeSelected(type.key, id))
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(typeRecordLabel(type.key, getRecordById(type.key, id))), 1)
                            ]),
                            _: 2
                          }, 1032, ["onClick:close"]))
                        }), 128))
                      ])
                    }), 64))
                  ]),
                  _createVNode(_component_v_btn, {
                    color: "primary",
                    block: "",
                    large: "",
                    class: "mt-6",
                    loading: transferring.value,
                    disabled: !userReceiver.value || totalSelected.value === 0,
                    onClick: transferRecords
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(" Transferir " + _toDisplayString(totalSelected.value) + " registro", 1),
                      (totalSelected.value !== 1)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_8, "s"))
                        : _createCommentVNode("", true)
                    ]),
                    _: 1
                  }, 8, ["loading", "disabled"]),
                  _createVNode(_component_v_btn, {
                    color: "warning",
                    variant: "outlined",
                    block: "",
                    class: "mt-3",
                    disabled: !userReceiver.value || totalRecords.value === 0,
                    onClick: _cache[5] || (_cache[5] = ($event: any) => (showTransferAllDialog.value = true))
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(" Reasignar todos los " + _toDisplayString(totalRecords.value) + " registros ", 1)
                    ]),
                    _: 1
                  }, 8, ["disabled"])
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(_component_v_dialog, {
        modelValue: showTransferAllDialog.value,
        "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((showTransferAllDialog).value = $event)),
        "max-width": "520"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_card_title, { class: "text-h6" }, {
                default: _withCtx(() => _cache[8] || (_cache[8] = [
                  _createTextVNode("Confirmar reasignación total")
                ])),
                _: 1,
                __: [8]
              }),
              _createVNode(_component_v_card_text, null, {
                default: _withCtx(() => [
                  _createTextVNode(" Se reasignarán todos los " + _toDisplayString(totalRecords.value) + " registros de este usuario, incluidos clientes, prospectos, cotizaciones, oportunidades, actividades y ventas. Esta acción no se puede deshacer desde esta pantalla. ", 1)
                ]),
                _: 1
              }),
              _createVNode(_component_v_card_actions, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_spacer),
                  _createVNode(_component_v_btn, {
                    variant: "text",
                    disabled: transferring.value,
                    onClick: _cache[6] || (_cache[6] = ($event: any) => (showTransferAllDialog.value = false))
                  }, {
                    default: _withCtx(() => _cache[9] || (_cache[9] = [
                      _createTextVNode("Cancelar")
                    ])),
                    _: 1,
                    __: [9]
                  }, 8, ["disabled"]),
                  _createVNode(_component_v_btn, {
                    color: "warning",
                    loading: transferring.value,
                    onClick: transferAllRecords
                  }, {
                    default: _withCtx(() => _cache[10] || (_cache[10] = [
                      _createTextVNode("Reasignar todo")
                    ])),
                    _: 1,
                    __: [10]
                  }, 8, ["loading"])
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["modelValue"])
    ]),
    _: 1,
    __: [11]
  }))
}
}

})